'use client';

import { forwardRef, useCallback, useRef, type ChangeEvent, type ReactNode } from 'react';
import Button from '@mui/material/Button';
import { styled, useThemeProps } from '@mui/material/styles';

import { Upload } from './icons';

interface FileInputButtonProps {
    onSelect?: (files: FileList | null) => void;
    acceptedFileTypes?: string[];
    allowsMultiple?: boolean;
    icon?: ReactNode | false;
    buttonText?: string | ReactNode;
    variant?: 'contained' | 'outlined';
    size?: 'small' | 'medium' | 'large';
    className?: string;
    disabled?: boolean;
}

const FileInputButtonRoot = styled('span', {
    name: 'MuiFileInputButton',
    slot: 'root',
})({
    display: 'inline-flex',
});

const FileInputButtonHiddenInput = styled('input', {
    name: 'MuiFileInputButton',
    slot: 'hiddenInput',
})({
    display: 'none',
});

const FileInputButton = forwardRef<HTMLSpanElement, FileInputButtonProps>(
    function FileInputButton(inProps, ref) {
        const props = useThemeProps({ props: inProps, name: 'MuiFileInputButton' });
        const {
            onSelect,
            acceptedFileTypes,
            allowsMultiple = false,
            icon,
            buttonText = 'Browse files',
            variant = 'contained',
            size = 'medium',
            className,
            disabled,
        } = props;

        const inputRef = useRef<HTMLInputElement>(null);

        const handleClick = useCallback(() => {
            inputRef.current?.click();
        }, []);

        const handleChange = useCallback(
            (e: ChangeEvent<HTMLInputElement>) => {
                onSelect?.(e.target.files);
                // Reset so the same file can be selected again
                if (inputRef.current) {
                    inputRef.current.value = '';
                }
            },
            [onSelect]
        );

        const accept = acceptedFileTypes?.join(',');

        return (
            <FileInputButtonRoot ref={ref} className={className}>
                <FileInputButtonHiddenInput
                    ref={inputRef}
                    type="file"
                    accept={accept}
                    multiple={allowsMultiple}
                    onChange={handleChange}
                />
                <Button
                    variant={variant}
                    size={size}
                    disabled={disabled}
                    onClick={handleClick}
                    startIcon={icon !== false ? (icon ?? <Upload />) : undefined}>
                    {buttonText}
                </Button>
            </FileInputButtonRoot>
        );
    }
);

export { FileInputButton };
export type { FileInputButtonProps };
